import { Skeleton, useColorModeValue } from "@chakra-ui/react";
import { SaasRoleName } from "@intentsify/types";
import { useUserAgencyCompany } from "queries/companies/useUserAgencyCompany";
import { useInvalidatePortalQueries } from "queries/useInvalidatePortalQueries";
import { Suspense, useCallback } from "react";
import { useNavigate } from "react-router";
import { useAtomValue } from "jotai";
import {
  DigitalForecastingScreenDefinition,
  LeadPacingScreenDefinition,
  PageTrackingScreenDefinition,
  WebAnalyticsScreenDefinition,
} from "screens";
import { DisplayPerformanceScreenDefinition } from "screens/CampaignPacing/screens/DisplayPerformance/DisplayPerformance.definition";
import { ContentLibraryDefinition } from "screens/ContentLibrary/ContentLibrary.definition";
import { EditAutomatedProgrammaticSegmentDefinition } from "screens/MediaActivation/screens/ProgrammaticDomains/screens/EditAutomatedProgrammaticSegment/EditAutomatedProgrammaticSegment.definition";
import { CreateAutomatedProgrammaticSegmentDefinition } from "screens/MediaActivation/screens/ProgrammaticDomains/screens/CreateAutomatedProgrammaticSegment/CreateAutomatedProgrammaticSegment.definition";
import { TargetPersonasDefinition } from "screens/TargetPersonas/TargetPersonas.definition";
import { useUser } from "store/store.hooks";
import { useAppStore } from "store/useAppStore";
import { ProgrammaticDomainsUploadScreen } from "types";
import { contentLibraryUpload } from "upload/contentLibraryUpload";
import { screenAtom } from "utils";
import { UserAgencyCompanySelect } from "./UserAgencyCompanySelect";

export const UserAgencyCompanySelector = () => {
  const startColor = useColorModeValue("blackAlpha.800", "blackAlpha.500");
  const endColor = useColorModeValue("blackAlpha.500", "blackAlpha.200");
  const isUploadingToContentLibrary = contentLibraryUpload.useIsUploading();
  const navigate = useNavigate();

  const user = useUser();
  const impersonation = useAppStore.use.impersonation();

  const invalidatePortalQueries = useInvalidatePortalQueries();
  // when user is in impersonation we need to fetch
  // the company of the impersonated user
  useUserAgencyCompany({
    onSuccess: () => {
      if (impersonation) {
        invalidatePortalQueries();
      }
    },
  });

  const screen = useAtomValue(screenAtom);

  // Navigate to clear screen-specific URLs before company change
  const handleBeforeCompanyChange = useCallback(() => {
    if (screen.name === DisplayPerformanceScreenDefinition.name) {
      navigate(
        DisplayPerformanceScreenDefinition.navigate({
          programmaticCampaignId: undefined,
          view: undefined,
        }),
        { replace: true }
      );
    }
  }, [screen.name, navigate]);

  // Show the company selector for admins on specific screens
  // because this selector is used to select the company for these screens
  if (
    user?.saasRole === SaasRoleName.Admin &&
    screen.name !== LeadPacingScreenDefinition.name &&
    screen.name !== ContentLibraryDefinition.name &&
    screen.name !== TargetPersonasDefinition.name &&
    screen.name !== ProgrammaticDomainsUploadScreen &&
    screen.name !== DigitalForecastingScreenDefinition.name &&
    screen.name !== WebAnalyticsScreenDefinition.name &&
    screen.name !== PageTrackingScreenDefinition.name &&
    screen.name !== EditAutomatedProgrammaticSegmentDefinition.name &&
    screen.name !== CreateAutomatedProgrammaticSegmentDefinition.name &&
    screen.name !== DisplayPerformanceScreenDefinition.name
  ) {
    return null;
  }

  if (user?.saasRole !== SaasRoleName.Admin && !user?.agencyId) {
    return null;
  }

  return (
    <Suspense
      fallback={
        <Skeleton
          startColor={startColor}
          endColor={endColor}
          h="32.39px"
          w="180px"
          borderRadius="md"
        />
      }
    >
      <UserAgencyCompanySelect
        isDisabled={isUploadingToContentLibrary}
        onBeforeCompanyChange={handleBeforeCompanyChange}
      />
    </Suspense>
  );
};
